import type { Annotation } from "@/types/annotation";
import { PromptingData } from "./types";
import { MediaItem } from "@/types/media";
import { PromptLabel } from "@/store/slices/modelIntentSlice";
import { calculateBoxPromptConfidenceThreshold } from "@/utils/predictions";

/**
 * Separate labels into two groups based on whether they have diffs (user edits)
 * - Labels with diffs: should use OWLv2 box prompting (visual similarity)
 * - Labels without diffs: should use SAM3 text prompting
 */
export function separateLabelsByDiffStatus(promptLabels: Map<string, PromptLabel>): {
    labelsWithDiffs: string[];
    labelsWithoutDiffs: string[];
} {
    const labelsWithDiffs: string[] = [];
    const labelsWithoutDiffs: string[] = [];

    promptLabels.forEach((label, name) => {
        if (label.hasDiffs) {
            labelsWithDiffs.push(name);
        } else {
            labelsWithoutDiffs.push(name);
        }
    });

    return { labelsWithDiffs, labelsWithoutDiffs };
}

/**
 * Build box prompting data for labels that have diffs.
 * Groups annotations by frame and builds the prompting payload for OWLv2.
 *
 * @param annotations - All annotations across all files/frames
 * @param labelsWithDiffs - Labels that have user edits (will use box prompting)
 * @param mediaDimensions - Map of frameId to dimensions
 * @param workspaceUrl - Workspace URL prefix
 * @returns Object containing box prompting data and calculated confidence threshold
 */
export function buildBoxPromptingDataForDiffLabels(
    annotations: Record<string, Record<string, Annotation[]>>,
    labelsWithDiffs: string[],
    mediaDimensions: Map<string, { width: number; height: number }>,
    workspaceUrl: string
): { promptingData: PromptingData[]; confidenceThreshold: number } {
    if (labelsWithDiffs.length === 0) {
        return { promptingData: [], confidenceThreshold: 0 };
    }

    // Collect all non-draft annotations for labels with diffs, grouped by frame
    const annotationsByFrame = new Map<string, Annotation[]>();
    let totalPromptCount = 0;

    Object.entries(annotations).forEach(([_fileId, frames]) => {
        Object.entries(frames).forEach(([frameId, frameAnnotations]) => {
            const relevantAnnotations = frameAnnotations.filter(
                (anno) =>
                    !anno.draft &&
                    anno.width > 0 &&
                    anno.height > 0 &&
                    labelsWithDiffs.includes(anno.label)
            );

            if (relevantAnnotations.length > 0) {
                if (!annotationsByFrame.has(frameId)) {
                    annotationsByFrame.set(frameId, []);
                }
                annotationsByFrame.get(frameId)!.push(...relevantAnnotations);
                totalPromptCount += relevantAnnotations.length;
            }
        });
    });

    if (annotationsByFrame.size === 0) {
        return { promptingData: [], confidenceThreshold: 0 };
    }

    // Build prompting data for each frame
    const promptingData: PromptingData[] = [];

    annotationsByFrame.forEach((frameAnnotations, frameId) => {
        const dimensions = mediaDimensions.get(frameId);
        if (!dimensions) {
            console.warn(
                `[buildBoxPromptingDataForDiffLabels] No dimensions found for frame ${frameId}`
            );
            return;
        }

        promptingData.push({
            file_uuid: `${workspaceUrl}/${frameId}`,
            width: dimensions.width,
            height: dimensions.height,
            boxes: frameAnnotations.map((anno) => ({
                x: Math.round(anno.x),
                y: Math.round(anno.y),
                w: Math.round(anno.width),
                h: Math.round(anno.height),
                negative: false,
                cls: anno.label
            }))
        });
    });

    // Calculate confidence threshold based on total prompt count
    const confidenceThreshold = calculateBoxPromptConfidenceThreshold(totalPromptCount);

    return { promptingData, confidenceThreshold };
}

/**
 * Build custom prompting data from annotations
 */
export function buildPromptingData(
    annotations: Annotation[],
    mediaItem: MediaItem,
    frameId: string,
    workspaceUrl: string
): PromptingData[] {
    // Filter out draft annotations
    const nonDraftAnnotations = annotations.filter((anno) => !anno.draft);

    if (nonDraftAnnotations.length === 0) {
        return [];
    }

    const width =
        mediaItem.metadata?.naturalDimensions?.width ||
        mediaItem.metadata?.serverMediaDimensions?.width ||
        0;

    const height =
        mediaItem.metadata?.naturalDimensions?.height ||
        mediaItem.metadata?.serverMediaDimensions?.height ||
        0;

    return [
        {
            file_uuid: `${workspaceUrl}/${frameId}`,
            width,
            height,
            boxes: nonDraftAnnotations.map((annotation) => ({
                x: Math.round(annotation.x),
                y: Math.round(annotation.y),
                w: Math.round(annotation.width),
                h: Math.round(annotation.height),
                negative: false,
                cls: annotation.label || "object"
            }))
        }
    ];
}

/**
 * Validate if prompting data has valid boxes
 */
export function hasValidPromptingData(promptingData: PromptingData[]): boolean {
    return (
        promptingData.length > 0 &&
        promptingData[0].boxes.length > 0 &&
        promptingData[0].boxes.every((box) => box.cls && box.cls !== "")
    );
}

/**
 * Create a function to mark annotations as draft
 * This returns a function that can be used with setState
 */
export function createMarkAnnotationsAsDraft(fileId: string, frameId: string) {
    return (prev: Record<string, Record<string, Annotation[]>>) => {
        const current = prev[fileId] || {};
        current[frameId] = current[frameId]?.map((anno) => ({ ...anno, draft: true })) || [];

        return { ...prev, [fileId]: current };
    };
}
